import React, { createContext, useContext, useEffect, useState } from "react";
import { firestore, storage } from "utils/firebase";

import firebase from "firebase/compat/app";
import { useAuth } from "./AuthContext";
import { useUsers } from "./UserContext";

export const MessagesContext = createContext();

export const MessagesProvider = ({ children }) => {
  const { currentAuth } = useAuth();
  const { userData } = useUsers();

  const [filteredChats, setFilteredChats] = useState([]);
  const [adminChats, setAdminChats] = useState([]);
  const [selectedAdminChat, setSelectedAdminChat] = useState(null);
  const [adminMessages, setAdminMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);

  const [startFetchGetAdminMessages, setStartFetchGetAdminMessages] =
    useState(false);

  useEffect(() => {
    if (!currentAuth || !startFetchGetAdminMessages || !userData.length) return;
    setLoading(true);
    var adminSubscription = firestore
      .collection("adminChats")
      .orderBy("lastMessage.lastUpdated", "desc")
      .limit(100)
      .onSnapshot(
        (querySnapshot) => {
          let chats = [];
          querySnapshot.forEach((doc) => {
            const user = userData.find((x) => x.id === doc.data().userId);
            if (!user) return;
            let chat = {
              ...doc.data(),
              id: doc.id,
              isAdmin: true,
              user,
            };
            chats.push(chat);
          });

          setAdminChats(chats);
          setLoading(false);
        },
        (error) => {
          console.log(
            "error listening for admin chat changes",
            error,
            currentAuth?.uid
          );
        }
      );
    return () => {
      adminSubscription();
    };
  }, [currentAuth, startFetchGetAdminMessages, userData]);

  useEffect(() => {
    if (!selectedAdminChat?.id) {
      setAdminMessages([]);
      setMessagesLoading(false);
      return;
    }
    setAdminMessages([]);
    setMessagesLoading(true);
    var subscription = firestore
      .collection("adminChats")
      .doc(selectedAdminChat.id)
      .collection("messages")
      .orderBy("createdAt", "asc")
      .onSnapshot(
        (querySnapshot) => {
          let fetchedTexts = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            let text = {
              _id: doc.id,
              text: data.text,
              createdAt: data.createdAt?.toDate?.() ?? new Date(0),
              user: data.user,
            };
            fetchedTexts.push(text);
          });
          setAdminMessages(fetchedTexts);
          setMessagesLoading(false);
        },
        (error) => {
          console.log(
            "error listening for admin message changes",
            error,
            currentAuth?.uid
          );
          setMessagesLoading(false);
        }
      );
    return () => subscription();
  }, [selectedAdminChat?.id]);

  useEffect(() => {
    if (!adminChats) return;
    if (filter === "active") {
      setFilteredChats(adminChats.filter((chat) => chat.active));
    } else {
      setFilteredChats(adminChats.filter((chat) => !chat.active));
    }
  }, [filter, adminChats]);

  const sendText = (text) => {
    const id = selectedAdminChat.id;
    const userId = selectedAdminChat.userId;
    const collection = "adminChats";

    const message = {
      text,
      createdAt: new Date(),
      user: {
        _id: "admin",
        name: "Admin",
      },
      userId,
    };

    const isFirstMessage = !selectedAdminChat.lastMessage;

    firestore
      .collection(collection)
      .doc(id)
      .collection("messages")
      .add(message)
      .then(() => {
        firestore
          .collection(collection)
          .doc(id)
          .update({
            lastMessage: {
              text: text,
              lastUpdated: new Date(),
              userId: "admin",
            },
            lastMessageSeenBy: [],
            ...(isFirstMessage && { active: true }),
          });
      });
  };

  const createChat = async (userId) => {
    const existingChat = adminChats.find((chat) => chat.userId === userId);

    if (existingChat) {
      setSelectedAdminChat(existingChat);
      return existingChat;
    }

    let parentMessage = {
      userId,
      lastMessage: null,
      createdAt: new Date(),
      updatedAt: new Date(),
      active: true,
    };

    try {
      const docRef = await firestore
        .collection("adminChats")
        .add(parentMessage);

      parentMessage.id = docRef.id;

      const user = userData.find((x) => x.id === userId);
      if (user) {
        parentMessage.user = user;
      }

      setSelectedAdminChat(parentMessage);
      return parentMessage;
    } catch (error) {
      console.error("Error creating chat:", error);
      throw error;
    }
  };

  const checkIfChatExists = (userId) => {
    return adminChats.find((chat) => chat.userId === userId);
  };

  const updateSeen = () => {
    firestore
      .collection("adminChats")
      .doc(selectedAdminChat.id)
      .update({
        lastMessageSeenBy: firebase.firestore.FieldValue.arrayUnion("admin"),
      });
  };

  const getUsersPromise = (chat) => {
    return new Promise(async (resolve, reject) => {
      let userDoc = await firestore.collection("users").doc(chat.userId).get();

      if (userDoc.exists) {
        let user = {
          id: userDoc.id,
          ...userDoc.data(),
        };

        chat.user = user;
        const profilePicRef = storage
          .ref()
          .child("defaultPictures/" + user.id + ".jpg");
        const url = await profilePicRef.getDownloadURL().catch((e) => {});
        if (!url) resolve(null);
        chat.user.defaultPic = url;
        resolve(chat);
      } else {
        resolve(null);
      }
    });
  };

  return (
    <MessagesContext.Provider
      value={{
        sendText,
        createChat,
        checkIfChatExists,
        adminChats,
        setAdminChats,
        filteredChats,
        setFilteredChats,
        selectedAdminChat,
        setSelectedAdminChat,
        adminMessages,
        setAdminMessages,
        messagesLoading,
        filter,
        setFilter,
        loading,
        setStartFetchGetAdminMessages,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = () => useContext(MessagesContext);
